<template>
  <div class="device-categories">
    <div class="device-categories__wrapper">
      <h1 class="device-categories__title">
        {{ _T("@Household appliance power calculator") }}
      </h1>
      <WarningMessage
        class="device-categories__warning"
        :title="_T('@Important')"
        :description="
          _T(
            '@This calculator is for calculating the power of home electrical appliances',
          )
        "
      />

      <div class="device-categories__categories-wrp">
        <DeviceCategory
          v-for="category in CATEGORIES"
          :key="category.head.categoryName"
          :categories="category"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import WarningMessage from "~~/modules/calculators/powerCalculator/components/sections/WarningMessage.vue";
import DeviceCategory from "~~/modules/calculators/powerCalculator/components/sections/DeviceCategory.vue";

const CATEGORIES = [
  {
    head: {
      icon: "media",
      title: _T("@Media and Gadgets"),
      categoryName: "MEDIA_AND_GADGETS",
    },
    items: [
      {
        index: 0,
        icon: "smartphone",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "smartphone",
        title: "@Smartphone",
        altImg: "@Smartphone power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [5, 10, 15, 30, 60, 100],
        defaultSelected: 10,
        selectedPower: 10,
      },
      {
        index: 1,
        icon: "tablet",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "tablet",
        title: "@tablet",
        altImg: "@Power consumption tablet",
        list: [1, 2, 3, 4, 5],
        powerData: [10, 15, 20],
        defaultSelected: 15,
        selectedPower: 15,
      },
      {
        index: 2,
        icon: "notebook",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "notebook",
        title: "@Laptop",
        altImg: "@Laptop Power Consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [45, 60, 85, 100, 120],
        defaultSelected: 60,
        selectedPower: 60,
      },
      {
        index: 3,
        icon: "pc",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "pc",
        title: "@Computer",
        altImg: "@Computer Power Consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [100, 200, 300, 500, 800, 1000],
        defaultSelected: 300,
        selectedPower: 300,
      },
      {
        index: 4,
        icon: "router",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "router",
        title: "@Wi-Fi router",
        altImg: "@Wi-Fi router power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [3, 5, 8, 10, 15],
        defaultSelected: 8,
        selectedPower: 8,
      },
      {
        index: 5,
        icon: "monitor",
        timeUni: "hours",
        timeValue: 1,
        itemName: "monitor",
        title: "@Monitor",
        altImg: "@Monitor power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [30, 50, 70, 100],
        defaultSelected: 50,
        selectedPower: 50,
      },
      {
        index: 6,
        icon: "tv",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "tv",
        title: "@TV",
        altImg: "@TV power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [50, 75, 100, 150],
        defaultSelected: 75,
        selectedPower: 75,
      },
      {
        index: 7,
        icon: "game-console",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "gameConsole",
        title: "@Game Console",
        altImg: "@PlayStation / XBOX Power Consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [100, 150, 200],
        defaultSelected: 150,
        selectedPower: 150,
      },
      {
        index: 8,
        icon: "converter",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "opticalConverter",
        title: "@Optical Converter",
        altImg: "@Power consumption of optical converter",
        powerData: [3, 5, 7, 10],
        defaultSelected: 5,
        selectedPower: 5,
      },
      {
        index: 9,
        icon: "starlink",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "starlink",
        title: "@Starlink",
        altImg: "@Power consumption of Starlink",
        powerData: [50, 75],
        defaultSelected: 50,
        selectedPower: 50,
      },
    ],
  },
  {
    head: {
      icon: "household_app",
      title: _T("@Household Appliances"),
      categoryName: "HOUSEHOLD_APPLIANCES",
    },
    items: [
      {
        index: 0,
        icon: "fridge",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "fridge",
        title: "@Refrigerator",
        altImg: "@Refrigerator Power Consumption",
        powerData: [50, 100, 150, 200],
        defaultSelected: 100,
        selectedPower: 100,
        startingPowerDemand: "×3-5",
        startupCoefficient: 3,
      },
      {
        index: 1,
        icon: "dishwasher",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "dishwasher",
        title: "@Dishwasher",
        altImg: "@Power consumption of a dishwasher",
        powerData: [1200, 1800, 2400],
        defaultSelected: 1800,
        selectedPower: 1800,
      },
      {
        index: 2,
        icon: "electric_stove",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "electricStove",
        title: "@Electric stove",
        altImg: "@Electric stove power consumption",
        powerData: [800, 1000, 1200, 1500, 2000, 2500, 3000],
        defaultSelected: 1000,
        selectedPower: 1000,
      },
      {
        index: 3,
        icon: "kettle",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "kettle",
        title: "@Electric kettle",
        altImg: "@Electric kettle power consumption",
        powerData: [1800, 2200, 2600],
        defaultSelected: 2200,
        selectedPower: 2200,
      },
      {
        index: 4,
        icon: "microwave",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "microwave",
        title: "@Microwave oven",
        altImg: "@Power consumption of a microwave oven",
        powerData: [600, 800, 1000, 1200],
        defaultSelected: 800,
        selectedPower: 800,
        startingPowerDemand: "×2-3",
        startupCoefficient: 2,
      },
      {
        index: 5,
        icon: "washing_machine",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "washingMachine",
        title: "@Washing machine",
        altImg: "@Power consumption of a washing machine",
        powerData: [500, 1000, 1500],
        defaultSelected: 1000,
        selectedPower: 1000,
        startingPowerDemand: "×2-3",
        startupCoefficient: 2,
      },
      {
        index: 6,
        icon: "washing_machine",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "dryerMachine",
        title: "@Dryer",
        altImg: "@Power consumption of a tumble dryer",
        powerData: [1500, 2000, 2500],
        defaultSelected: 2000,
        selectedPower: 2000,
        startingPowerDemand: "х1.25-1.5",
        startupCoefficient: 1.25,
      },
      {
        index: 7,
        icon: "kettle",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "coffeeMachine",
        title: "@Coffee machine",
        altImg: "@Power consumption of the coffee machine",
        powerData: [750, 1000, 1200, 1500],
        defaultSelected: 1000,
        selectedPower: 1000,
      },
      {
        index: 8,
        icon: "freezer",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "freezer",
        title: "@Freezer",
        altImg: "@Power consumption of the freezer",
        powerData: [170, 200, 240],
        defaultSelected: 200,
        selectedPower: 200,
      },
      {
        index: 9,
        icon: "toaster",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "toaster",
        title: "@Toaster",
        altImg: "@Power consumption of a toaster",
        powerData: [800, 1200, 1500],
        defaultSelected: 1200,
        selectedPower: 1200,
      },
      {
        index: 10,
        icon: "oven",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "oven",
        title: "@Oven",
        altImg: "@Oven Power Consumption",
        powerData: [1500, 1800, 2100],
        defaultSelected: 1800,
        selectedPower: 1800,
      },
      {
        index: 11,
        icon: "multicooker",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "multicooker",
        title: "@Multicooker",
        altImg: "@Power consumption of a multicooker",
        powerData: [400, 600, 800],
        defaultSelected: 400,
        selectedPower: 400,
        startingPowerDemand: "×3",
        startupCoefficient: 3,
      },
      {
        index: 12,
        icon: "iron",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "iron",
        title: "@Iron",
        altImg: "@Iron power consumption",
        powerData: [1800, 2200, 2600],
        defaultSelected: 2200,
        selectedPower: 2200,
      },
      {
        index: 13,
        icon: "iron",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "steamer",
        title: "@Steamer",
        altImg: "@Steamer power consumption",
        powerData: [1000, 1500, 2000],
        defaultSelected: 1500,
        selectedPower: 1500,
      },
      {
        index: 14,
        icon: "hairdryer",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "hairdryer",
        title: "@Hair dryer",
        altImg: "@Power consumption of the hair dryer",
        powerData: [1800, 2200, 2600],
        defaultSelected: 2200,
        selectedPower: 2200,
      },
    ],
  },
  {
    head: {
      icon: "led",
      title: _T("@Climate and lighting"),
      categoryName: "CLIMATE_AND_LIGHTING",
    },
    items: [
      {
        index: 0,
        icon: "led",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "ledLamp",
        title: "@LED lamp",
        altImg: "@LED lamp power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [5, 10, 15, 20],
        defaultSelected: 10,
        selectedPower: 10,
      },
      {
        index: 1,
        icon: "fan",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "fan",
        title: "@Fan",
        altImg: "@Fan power consumption",
        list: [1, 2, 3, 4, 5],
        powerData: [30, 50, 70],
        defaultSelected: 50,
        selectedPower: 50,
        startingPowerDemand: "×1.5-2",
        startupCoefficient: 1.5,
      },
      {
        index: 2,
        icon: "boiler",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "boiler",
        title: "@Boiler",
        altImg: "@Boiler Power Consumption",
        powerData: [1700, 2000, 2300],
        defaultSelected: 2000,
        selectedPower: 2000,
      },
      {
        index: 3,
        icon: "gas_boiler",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "gasBoiler",
        title: "@Gas boiler",
        altImg: "@Power consumption of a gas boiler",
        powerData: [40, 60, 100, 120, 160],
        defaultSelected: 60,
        selectedPower: 60,
        startingPowerDemand: "×2-3",
        startupCoefficient: 2,
      },
      {
        index: 4,
        icon: "heater",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "heater",
        title: "@Heater",
        altImg: "@Power consumption of the heater",
        powerData: [1000, 1500, 2000],
        defaultSelected: 1500,
        selectedPower: 1500,
      },
      {
        index: 5,
        icon: "conditioner",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "conditioner",
        title: "@Air Conditioner",
        altImg: "@Air Conditioner Power Consumption",
        powerData: [500, 700, 900],
        defaultSelected: 700,
        selectedPower: 700,
        startingPowerDemand: "х3-4",
        startupCoefficient: 3,
      },
      {
        index: 6,
        icon: "heat_pump",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "heatPump",
        title: "@Heat pump",
        altImg: "@Heat pump power consumption",
        powerData: [1600, 2000, 2400],
        defaultSelected: 2000,
        selectedPower: 2000,
        startingPowerDemand: "х1.5-2",
        startupCoefficient: 1.5,
      },
      {
        index: 7,
        icon: "heater",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "electricBoiler",
        title: "@Electric boiler",
        altImg: "@Power consumption of an electric boiler",
        powerData: [2500, 3000, 3500],
        defaultSelected: 3000,
        selectedPower: 3000,
      },
    ],
  },
  {
    head: {
      icon: "other_adjustments",
      title: _T("@Other devices"),
      categoryName: "OTHER",
    },
    items: [
      {
        index: 0,
        icon: "option",
        timeUnit: "hours",
        timeValue: 1,
        itemName: "ownOption",
        title: "@Your own option",
        altImg: "@Your own optio",
        list: [1, 2, 3, 4, 5],
        powerData: [20, 100, 500, 1000, 1500, 3000],
        defaultSelected: 20,
        selectedPower: 20,
      },
    ],
  },
];
</script>

<style lang="scss" scoped>
.device-categories {
  width: 100%;

  border-radius: 24px;

  background-color: white;

  padding: 24px;

  @include bigMobile() {
    border-radius: 16px;

    padding: 16px 8px;
  }

  &__wrapper {
    max-width: 1130px;
    width: 100%;

    @include flex-container(column, null);

    gap: 16px;
    margin: 0 auto;
  }

  &__title {
    @include font(24, 32, 700);
    color: var(--color-primary-base);

    @include bigMobile() {
      text-align: center;
    }
  }

  &__warning {
    width: 100%;
  }

  &__categories-wrp {
    @include flex-container(column, null);

    gap: 32px;

    @include bigMobile() {
      gap: 24px;
    }
  }
}
</style>
